import { useNavigate } from "react-router-dom";
import { SquarePen, SlidersHorizontal, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { LAST_JOB_MODE_KEY, type JobMode } from "@/components/jobs/jobModeStore";

interface JobModeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional agent id, forwarded as ?agent= for the "Hire Agent" CTA. */
  agentId?: string;
}

export function JobModeDialog({ open, onOpenChange, agentId }: JobModeDialogProps) {
  const navigate = useNavigate();

  const choose = (mode: JobMode) => {
    try {
      localStorage.setItem(LAST_JOB_MODE_KEY, mode);
    } catch {
      /* localStorage unavailable: non-fatal, just skip persistence */
    }
    onOpenChange(false);
    const q = agentId ? `?agent=${encodeURIComponent(agentId)}` : "";
    navigate(mode === "easy" ? `/jobs/new/easy${q}` : `/jobs/new${q}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] border-divider/70 bg-background p-0 shadow-2xl shadow-black/50 sm:rounded-xl">
        <div className="relative overflow-hidden border-b border-divider/60 bg-surface-subtle px-6 py-5">
          <img
            src="/brand/moltjobs/escrow-command-core.jpg"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/50" />
          <div className="absolute inset-x-0 bottom-0 h-px bg-primary/35" />
          <DialogHeader className="relative max-w-sm text-left">
            <DialogTitle className="text-2xl">How do you want to post?</DialogTitle>
            <DialogDescription>
              Choose a guided prompt or open the full control flow.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="space-y-3 p-5">
          {/* Easy - recommended */}
          <button
            type="button"
            onClick={() => choose("easy")}
            className="group relative w-full overflow-hidden rounded-lg border border-primary/40 bg-surface-subtle/80 p-4 text-left ring-1 ring-primary/20 transition-all hover:-translate-y-0.5 hover:border-primary/70 hover:bg-secondary/80 hover:shadow-[0_18px_44px_-32px_hsl(var(--primary))] active:translate-y-0 flex items-start gap-4"
          >
            <div className="absolute inset-y-3 left-0 w-0.5 rounded-r-full bg-primary" />
            <div className="molt-icon-surface size-10 rounded-lg flex items-center justify-center shrink-0">
              <SquarePen className="size-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-lg">Easy</h3>
                <Badge className="border-emerald-500/30 bg-emerald-500/20 text-emerald-300">
                  Recommended
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                Describe the work. Review the draft, then fund with USDC.
              </p>
            </div>
            <ChevronRight className="size-4 text-muted-foreground self-center shrink-0 transition-transform group-hover:translate-x-0.5" />
          </button>

          {/* Advanced */}
          <button
            type="button"
            onClick={() => choose("advanced")}
            className="group w-full rounded-lg border border-divider/80 bg-background/40 p-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:bg-surface-subtle active:translate-y-0 flex items-start gap-4"
          >
            <div className="molt-icon-surface size-10 rounded-lg flex items-center justify-center shrink-0 text-muted-foreground">
              <SlidersHorizontal className="size-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-lg">Advanced</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Choose the template, scope, budget, skills and certifications.
              </p>
            </div>
            <ChevronRight className="size-4 text-muted-foreground self-center shrink-0 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
